import React, {
  createRef,
  CSSProperties,
  ReactElement,
  RefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Table, Button, Modal, Container, Row as BSRow } from 'react-bootstrap';
import { Form } from 'react-final-form';
import {
  HeaderDisplayKeysWithType,
  OnRowRender,
  RowRenderResult,
} from './EditableTable';
import { Decorator } from 'final-form';

type TValue = {
  editMetadata?: boolean;
  [k: string]: unknown;
};

type EditingTransform<T, K> = (v: T) => K;

type RowProps<T extends Record<string, unknown>, K extends T> = {
  idx: number;
  rowStyle?: CSSProperties;
  rowClassName?: string;
  editable: boolean;
  deleteable?: boolean;
  scrollable?: boolean;
  headers: HeaderDisplayKeysWithType<T, keyof T>[];
  value: T;
  index: number;
  fixCol?: number;
  fixLeftOffset?: number[];
  onRowUpdating: (value: T) => void;
  onRowCancelUpdate: (value: T, cancel: boolean) => void;
  onValueUpdate: (value: T) => void;
  onValueDelete?: (value: T) => void;
  beforeEdit?: EditingTransform<T, K>;
  beforeDelete?: EditingTransform<T, K>;
  afterEdit?: EditingTransform<K, T>;
  afterDelete?: EditingTransform<K, T>;
  context?: Record<string, unknown>;
  decorators?: Array<Decorator<T, Partial<T>>>;
  colRefs: RefObject<HTMLDivElement>[];
  editing: boolean;
};

const EditableRow = <T extends Record<string, unknown>, K extends T>({
  idx,
  rowStyle,
  rowClassName,
  editable,
  deleteable,
  value: v,
  index,
  headers,
  fixLeftOffset = [],
  onRowUpdating,
  onRowCancelUpdate,
  onValueUpdate,
  onValueDelete,
  beforeEdit = (v) => v as K,
  beforeDelete = (v) => v as K,
  afterEdit = (v) => v as T,
  afterDelete = (v) => v as T,
  decorators,
  context = {},
  colRefs,
  editing,
}: RowProps<T, K>) => {
  // const [editing, setEditing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const editRowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!editing) {
      return;
    }

    const frameId = window.requestAnimationFrame(() => {
      const container = editRowRef.current;
      if (!container) {
        return;
      }

      const firstInput = container.querySelector<HTMLElement>(
        'input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled])'
      );

      if (!firstInput || firstInput.offsetParent === null) {
        return;
      }

      firstInput.focus();
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [editing]);

  const focusNextEditableControl = (
    container: HTMLElement,
    currentTarget: HTMLElement,
    reverse: boolean
  ) => {
    const controls = Array.from(
      container.querySelectorAll<HTMLElement>(
        'input:not([type="hidden"]), select, textarea, button, [tabindex]:not([tabindex="-1"])'
      )
    ).filter(
      (el) =>
        !el.hasAttribute('disabled') &&
        el.getAttribute('aria-hidden') !== 'true' &&
        el.offsetParent !== null
    );

    if (controls.length === 0) {
      return;
    }

    const currentIndex = controls.findIndex((el) => el === currentTarget);
    if (currentIndex === -1) {
      controls[0].focus();
      return;
    }

    const nextIndex = reverse
      ? (currentIndex - 1 + controls.length) % controls.length
      : (currentIndex + 1) % controls.length;
    controls[nextIndex].focus();
  };

  const handleEditRowKeyDownCapture = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key !== 'Tab' && event.key !== 'Enter') {
      return;
    }

    const target = event.target as HTMLElement | null;
    const container = event.currentTarget as HTMLElement;
    if (!target) {
      return;
    }

    // Keep textarea Enter behavior for multiline input.
    if (event.key === 'Enter' && target.tagName.toLowerCase() === 'textarea') {
      return;
    }

    const tagName = target.tagName.toLowerCase();
    if (
      tagName !== 'input' &&
      tagName !== 'select' &&
      tagName !== 'textarea' &&
      tagName !== 'button'
    ) {
      return;
    }

    // Keep native Enter click behavior for buttons.
    if (event.key === 'Enter' && tagName === 'button') {
      return;
    }

    event.preventDefault();
    focusNextEditableControl(container, target, event.shiftKey);
  };
  const row = headers.reduce(
    (results, h, colIdx) => {
      const value = h.onRender
        ? h.onRender(v[h.property], index, editing, { context, allValues: v })
        : v[h.property];
      if (fixLeftOffset && fixLeftOffset[colIdx + 1] !== undefined) {
        const ref = createRef<HTMLDivElement>();
        colRefs.push(ref);
        results.sticky.push(
          <div
            ref={ref}
            className="div-table-cell div-sticky-left"
            style={{
              ...h.style,
              left: fixLeftOffset[1 + colIdx],
            }}
            key={`body-${index}-sticky-${String(h.property)}`}
          >
            {value as ReactElement}
          </div>
        );
      } else {
        results.normal.push(
          <div
            style={h.style}
            key={`body-${index}-${String(h.property)}`}
            className="text-break div-table-cell"
          >
            {value as ReactElement}
          </div>
        );
      }
      return results;
    },
    {
      sticky: [] as ReactElement[],
      normal: [] as ReactElement[],
      stickyRefs: [] as RefObject<HTMLDivElement>[],
    }
  );

  const deleteButton = deleteable ? (
    <>
      <Button
        key="cell-edit"
        className="border-0 px-2 py-1 m-0 mr-1"
        variant="outline-danger"
        size="sm"
        onClick={() => {
          setDeleting(true);
        }}
      >
        <span className="feather icon-trash-2" />
      </Button>

      <Modal
        show={deleting}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        backdrop="static"
        centered
      >
        <Modal.Header>
          <Modal.Title id="contained-modal-title-vcenter">刪除</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Container>
            <BSRow className="mb-3">您確定要刪除嗎?</BSRow>
          </Container>
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="submit"
            variant="danger"
            onClick={() => {
              if (onValueDelete) {
                onValueDelete(v);
                console.log(v);
              }
              setDeleting(false);
            }}
          >
            確認
          </Button>
          <Button
            type="reset"
            variant="secondary"
            onClick={() => {
              setDeleting(false);
            }}
          >
            取消
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  ) : (
    <></>
  );

  const editButton = editable ? (
    <>
      <Button
        key="cell-edit"
        className="border-0 px-2 py-1 m-0 mr-1"
        variant="outline-primary"
        size="sm"
        onClick={() => {
          onRowUpdating(v);
        }}
      >
        <span className="feather icon-edit" />
      </Button>
    </>
  ) : (
    <></>
  );

  if (editable && editing) {
    const content = (
      <div
        ref={editRowRef}
        key={`body-${idx}-editing`}
        style={rowStyle}
        className={['div-table-row', rowClassName].join(' ')}
        onKeyDownCapture={handleEditRowKeyDownCapture}
      >
        <Form
          initialValues={beforeEdit(v)}
          decorators={decorators}
          onSubmit={(value) => {
            onRowCancelUpdate(v, false);
            onValueUpdate(afterEdit(value as K));
          }}
          validateOnBlur
          subscription={{ submitting: true, pristine: true }}
          render={({ values, handleSubmit }) => {
            const ref = createRef<HTMLDivElement>();
            colRefs.unshift(ref);
            row.sticky.unshift(
              <div
                className="div-table-cell div-sticky-left"
                key={`body-edit`}
                style={{ left: fixLeftOffset[0] }}
              >
                <Button
                  key="cell-save"
                  className="border-0 px-2 py-1 m-0 mr-1"
                  variant="outline-primary"
                  size="sm"
                  onClick={handleSubmit}
                >
                  <span className="feather icon-save" />
                </Button>
                <Button
                  key="cell-x"
                  className="border-0 px-2 py-1 m-0 mr-1"
                  variant="outline-danger"
                  size="sm"
                  onClick={() => {
                    onRowCancelUpdate(v, true);
                  }}
                >
                  <span className="feather icon-x" />
                </Button>
              </div>
            );
            return (
              <>
                {row.sticky}
                {row.normal}
              </>
            );
          }}
        />
      </div>
    );
    return content;
  } else {
    const ref = createRef<HTMLDivElement>();
    colRefs.unshift(ref);
    row.sticky.unshift(
      <div
        className="div-table-cell div-sticky-left"
        key={`body-edit`}
        style={{ left: fixLeftOffset[0] }}
      >
        {editButton}
        {deleteButton}
      </div>
    );
    return (
      <div
        key={`body-${idx}-readonly`}
        style={rowStyle}
        className={['div-table-row', rowClassName].join(' ')}
      >
        {row.sticky}
        {row.normal}
      </div>
    );
  }
};

type Props<T extends Record<string, unknown>, K extends T> = {
  headers: HeaderDisplayKeysWithType<T, keyof T>[];
  values: T[];
  fixCol?: number;
  responsive?: boolean;
  scrollable?: boolean;
  onValueUpdate: (value: T) => void;
  onValueDelete?: (value: T) => void;
  onRowUpdating?: (value: T) => void;
  onRowCancelUpdate?: (value: T) => void;
  onRowRender?: OnRowRender<T>;
  beforeEdit?: EditingTransform<T, K>;
  beforeDelete?: EditingTransform<T, K>;
  afterEdit?: EditingTransform<K, T>;
  afterDelete?: EditingTransform<K, T>;
  context?: Record<string, unknown>;
  decorators?: Array<Decorator<T, Partial<T>>>;
  autoNext?: boolean;
};

const inlineEditableTable = <T extends TValue, K extends T>({
  headers,
  values,
  fixCol,
  responsive,
  scrollable,
  decorators,
  onRowRender,
  onValueUpdate,
  onValueDelete,
  onRowUpdating,
  onRowCancelUpdate,
  beforeEdit,
  afterEdit,
  beforeDelete,
  afterDelete,
  context = {},
  autoNext,
}: Props<T, K>) => {
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const fixLeftStartOffset = 1;
  const headerRefs: RefObject<HTMLDivElement>[] = Array.from({
    length: fixLeftStartOffset + (fixCol || 0),
  }).map(() => useRef<HTMLDivElement>(null));
  const refLefts = useMemo(
    () => headerRefs.map((r) => r.current?.offsetLeft || 0),
    headerRefs.map((r) => r.current?.offsetLeft)
  );
  useEffect(() => {
    let offset = 0;
    headerRefs.forEach((s, i) => {
      s.current?.style.setProperty('left', `${offset}px`);
      bodyElements.forEach((row) => {
        row.colRefs[i]?.current?.style.setProperty('left', `${offset}px`);
      });
      offset += s.current?.offsetWidth || 0;
    });
  }, [values, ...refLefts]);
  const headerElements = headers.map((h, i) => {
    if (fixCol && i < fixCol) {
      return (
        <div
          ref={headerRefs[i + fixLeftStartOffset]}
          className="div-table-cell div-sticky-left"
          style={h.style}
          key={`header-${i}`}
        >
          {h.display}
        </div>
      );
    } else
      return (
        <div className="div-table-cell" style={h.style} key={`header-${i}`}>
          {h.display}
        </div>
      );
  });

  headerElements.unshift(
    <div
      ref={headerRefs[0]}
      className="div-table-cell div-sticky-left"
      style={{
        width: 2.2 + 'rem',
      }}
      key={`header-${headerElements.length}-edit`}
    ></div>
  );

  function endUpdateOrMoveNext(cancel: boolean) {
    if (!autoNext || cancel) {
      setEditingRowIndex(null);
      return;
    }
    setEditingRowIndex((prev) => {
      if (prev === null) return null;
      const nextIdx = prev + 1;
      if (nextIdx >= values.length) return null;
      return nextIdx;
    });
  }

  useEffect(() => {
    if (editingRowIndex !== null && autoNext) {
      onRowUpdating && onRowUpdating(values[editingRowIndex]);
    }
  }, [editingRowIndex]);

  const bodyElements = values.map((v, i) => {
    const {
      rowStyle,
      rowClassName,
      editable = true,
      deleteable = false,
    } = onRowRender ? onRowRender(v) : ({} as RowRenderResult);
    const colRefs: RefObject<HTMLDivElement>[] = [];
    const element = (
      <EditableRow<T, K>
        key={`body-row-${i}`}
        idx={i}
        rowStyle={rowStyle}
        rowClassName={rowClassName}
        editable={editable}
        deleteable={deleteable}
        headers={headers}
        value={v}
        index={i}
        fixLeftOffset={refLefts}
        fixCol={fixCol}
        onRowUpdating={(v) => {
          setEditingRowIndex(i);
        }}
        onValueUpdate={onValueUpdate}
        onValueDelete={onValueDelete}
        onRowCancelUpdate={(v, cancel) => {
          endUpdateOrMoveNext(cancel);
          onRowCancelUpdate && onRowCancelUpdate(v);
        }}
        beforeEdit={beforeEdit}
        afterEdit={afterEdit}
        beforeDelete={beforeDelete}
        afterDelete={afterDelete}
        context={context}
        decorators={decorators}
        colRefs={colRefs}
        editing={editingRowIndex === i}
      />
    );
    return {
      colRefs,
      element,
    };
  });

  let table = (
    <>
      <div
        className={
          'table table-bordered table-striped table-hover div-table text-center' +
          (scrollable ? ' div-table-max-content' : '')
        }
      >
        <div className="div-table-row div-sticky-top div-table-header">
          {headerElements}
        </div>
        {bodyElements.map((be) => be.element)}
      </div>
      {bodyElements.length === 0 ? (
        <div className="w-100 p-2 text-center border">目前資料表中沒有資料</div>
      ) : null}
    </>
  );

  if (responsive) {
    table = <div className="div-table-responsive">{table}</div>;
  }
  return table;
};

export const InlineEditableTable = inlineEditableTable;
